// F2 (events + playback) local data helpers — spec 17 §2/§4.
//
// WHY a local fetch helper exists: the events endpoint returns pagination as a
// TOP-LEVEL sibling of `data` ({data:[...], meta:{next_cursor}, request_id}),
// verified against the live dev API — but the shared lib/api.ts apiFetch()
// returns only the `data` member, dropping `meta`. Shared files must not be
// edited by feature agents, so we keep a minimal meta-aware GET here.
// INTEGRATOR NOTE: please promote a meta-aware variant into src/lib/api.ts and
// delete fetchListWithMeta below.

import type { EventAi, RingDesc } from '../../lib/eventAi';
import { apiFetch, ApiError, buildApiUrl, refreshAccessToken, tokenStore } from '../../lib/api';
import { config } from '../../config';
import i18n from '../../i18n';
import { getCachedSnapshot, putCachedSnapshot, blobToDataUrl } from './eventSnapshotDb';

// ---- meta-aware list fetch (GET only) ----

export interface ListResult<T> {
  items: T[];
  /** null when the server reports no further page. */
  nextCursor: string | null;
  requestId: string | null;
}

type Query = Record<string, string | number | boolean | null | undefined>;

export async function fetchListWithMeta<T>(path: string, query?: Query, signal?: AbortSignal, _retried = false): Promise<ListResult<T>> {
  const headers: Record<string, string> = {
    'X-Client-Id': config.clientId,
    'X-Client-Key': config.clientKey,
    'X-Client-Version': config.clientVersion,
  };
  const token = tokenStore.get();
  if (token) headers['Authorization'] = 'Bearer ' + token;

  const res = await fetch(buildApiUrl(path, query), { method: 'GET', headers, signal });

  if (res.status === 401 && !_retried) {
    // 20 §2.1:ACCOUNT_INACTIVE 是終局,不進 refresh 鏈(mirror lib/api.ts apiFetch)。
    let peek: { error?: { code?: string } } | null = null;
    try {
      peek = (await res.clone().json()) as { error?: { code?: string } } | null;
    } catch {
      peek = null; // body 解析不出(或環境無 clone)→ fail-safe 走 refresh
    }
    if (peek?.error?.code === 'ACCOUNT_INACTIVE') {
      throw new ApiError(401, 'ACCOUNT_INACTIVE', i18n.t('account.errAccountInactive'), res.headers.get('X-Request-Id'));
    }
    // Same single-flight refresh the shared client uses; a second 401 surfaces
    // as ApiError (AuthContext-owned onUnauthorized stays untouched — shared contract).
    await refreshAccessToken().catch(() => {
      throw new ApiError(401, 'UNAUTHORIZED', i18n.t('common.sessionExpired'), res.headers.get('X-Request-Id'));
    });
    return fetchListWithMeta<T>(path, query, signal, true);
  }

  const requestId = res.headers.get('X-Request-Id');
  const body = (await res.json().catch(() => ({}))) as {
    data?: T[];
    meta?: { next_cursor?: string | null };
    error?: { code?: string; message?: string };
  };
  if (!res.ok) {
    throw new ApiError(res.status, body.error?.code ?? `HTTP_${res.status}`, body.error?.message ?? `HTTP ${res.status}`, requestId);
  }
  return {
    items: body.data ?? [],
    nextCursor: body.meta?.next_cursor ?? null,
    requestId,
  };
}

// ---- event records (restful 00 §13) ----

export interface EventItem {
  /**
   * Capability-encoded 64-bit event id hex, e.g. "0x1100008041000001"
   * (common/03 §9.2 event_id_hex; REST 與 WSS event frame 同名同值)。
   * 型別細分（cluster bits [39:24]）一律以此欄為準（spec 17 §4.2）。
   */
  event_id: string;
  /**
   * 粗分類語意字串（restful 00 ch19 enum：motion/doorbell/manual/peer_device/
   * recording_clip）。僅作 event_id 缺失/非 hex 時的 fallback——**不是** hex，
   * 餵進 BigInt cluster 解析必炸（W2-03 的 bug 根源）。
   */
  event_type: string;
  /** Microsecond epoch. */
  event_ts: number;
  /** ADR-045 NVR/DVR 1-based channel; absent for whole-device events. */
  channel?: number;
  correlation_id?: string;
  /** eid / in-force record settings stamped on the event (00 §19; event-system dual-writes
   *  them). The recPlayback views label an event's storage by record_location. */
  eid?: string;
  /** 事件是否有可拉取的縮圖（restful 00 §19；true 時才呼叫 per-event snapshot 端點）。 */
  has_snapshot?: boolean;
  record_location?: string;
  record_mode?: string;
  record_quality?: string;
  /** ls-loop ME：AI 電腦的判斷（沒判斷過就沒有）。 */
  ai?: EventAi;
  /** 門鈴沒人接：雲端本地 AI 看按鈴截圖的說明（有就優先顯示）。 */
  ai_desc?: RingDesc;
  payload?: Record<string, unknown>;
}

/**
 * 無錄影事件（record_mode == "none"）：裝置當下未開啟錄影 → 此事件不可能有對應影片。
 * spec 17 §4.1「無錄影事件不顯示」——回放時間軸 marker / 事件清單 / 事件計數皆濾除。
 * 判定取事件自身的 record_mode 快照（非裝置現值），大小寫不敏感。
 */
/**
 * 事件清單的「錄影位置」角標（業主 2026-09-08 裁決 A；spec 17 §4）。每筆事件自帶 record_location
 * （事件當下錄在哪的快照），回放早已按它分流。缺值 / 不認得 → null（不標，也不參與混存判定）。
 * 鏡像 Android `eventStorageOf` / iOS `eventStorage(recordLocation:)`。
 */
export type EventStorage = 'cloud' | 'sdcard';
export function eventStorage(ev: { record_location?: string | null }): EventStorage | null {
  const v = (ev.record_location ?? '').trim().toLowerCase();
  if (v === 'cloud') return 'cloud';
  if (v === 'sdcard' || v === 'sd') return 'sdcard';
  return null;
}

/** 顯示中的事件是否同時含雲端與 SD 卡兩種位置——只有混存時才標角標，全是同一種就不標（噪音）。 */
export function eventStorageMixed(events: Array<{ record_location?: string | null }>): boolean {
  const seen = new Set<EventStorage>();
  for (const ev of events) {
    const s = eventStorage(ev);
    if (s) seen.add(s);
    if (seen.size >= 2) return true;
  }
  return false;
}

export function eventNoRecording(ev: { record_mode?: string | null }): boolean {
  return (ev.record_mode ?? '').toLowerCase() === 'none';
}

export interface LogbookItem {
  kind: 'event' | 'command' | string;
  ts_us: number;
  device_id?: string;
  event_id?: string;
  event_type?: string;
  channel?: number;
  correlation_id?: string;
  eid?: string;
  has_snapshot?: boolean;
  record_location?: string;
  record_mode?: string;
  record_quality?: string;
  ai?: EventAi;
  ai_desc?: RingDesc;
  action?: string;
  actor_user_id?: string;
  actor_role?: string;
  request_id?: string;
  payload?: Record<string, unknown>;
  meta?: Record<string, unknown>;
}

export interface EventsPageOpts {
  from?: number;
  to?: number;
  cursor?: string | null;
  limit?: number;
  signal?: AbortSignal;
}

/** GET /v3/devices/{id}/events — server returns ascending event_ts pages. */
export function fetchDeviceEvents(deviceId: string, opts: EventsPageOpts = {}): Promise<ListResult<EventItem>> {
  return fetchListWithMeta<EventItem>(
    `/v3/devices/${encodeURIComponent(deviceId)}/events`,
    {
      from: opts.from,
      to: opts.to,
      cursor: opts.cursor ?? undefined,
      limit: opts.limit ?? 100,
    },
    opts.signal
  );
}

/**
 * GET /v3/devices/{id}/events/{eid}/snapshot — 拉單一事件縮圖的短效簽名 URL（~300s）。
 * 僅對 `has_snapshot === true` 的列呼叫；404（無縮圖）與其他錯誤一律視為「無圖」
 * 退回 icon/dot（best-effort）。
 * ⚠️ 這是全 app 唯一的 per-event snapshot fetcher —— 事件清單與 recPlayback 事件時間軸共用。
 * （recRest.ts 曾有一支 legacy 非裝置範圍 /v3/events/{eid}/snapshot 讀 .url 的 fetchEventSnapshotUrl，
 * 端點與欄位都錯且零呼叫，已移除。）
 */
export async function fetchEventSnapshot(deviceId: string, eid: string, signal?: AbortSignal): Promise<string | null> {
  try {
    const res = await apiFetch<{ snapshot_url?: string }>(
      `/v3/devices/${encodeURIComponent(deviceId)}/events/${encodeURIComponent(eid)}/snapshot`,
      { signal }
    );
    return res.data?.snapshot_url ?? null;
  } catch {
    return null;
  }
}

// Per-session in-memory cache of the resolved thumbnail src (eid → data: URL, or the
// short-lived signed URL fallback). Avoids re-reading IndexedDB / re-fetching on re-render.
const snapshotMem = new Map<string, string>();

/**
 * Resolve an event thumbnail src with a LOCAL persistent cache (aligns iOS/Android's
 * on-device 90-day cache). Three tiers:
 *   1. in-memory (this session) → instant;
 *   2. IndexedDB (survives reload) → a stored `data:` URL;
 *   3. network → GET the signed snapshot URL, fetch its bytes (CORS is served), store
 *      them as a `data:` URL (img-src allows data:, not blob:) and persist to IndexedDB.
 * If the byte fetch fails (CORS/abort/offline) we FALL BACK to the signed URL itself so
 * the <img> still shows (current behavior) — the cache is best-effort, never a regression.
 * Returns null only when there is genuinely no snapshot. Purge of >90d entries is a
 * separate call (purgeExpiredSnapshots), made once on the events views' mount.
 */
export async function loadEventSnapshot(deviceId: string, eid: string, signal?: AbortSignal): Promise<string | null> {
  if (!eid) return null;
  const mem = snapshotMem.get(eid);
  if (mem) return mem;
  const cached = await getCachedSnapshot(eid);
  if (cached) {
    snapshotMem.set(eid, cached);
    return cached;
  }
  const url = await fetchEventSnapshot(deviceId, eid, signal);
  if (!url) return null;
  try {
    const res = await fetch(url, { signal });
    if (res.ok) {
      const dataUrl = await blobToDataUrl(await res.blob());
      snapshotMem.set(eid, dataUrl);
      void putCachedSnapshot(eid, dataUrl); // best-effort persist (90-day self-delete)
      return dataUrl;
    }
  } catch {
    // CORS / aborted / offline → fall back to the signed URL below (no persistence).
  }
  snapshotMem.set(eid, url);
  return url;
}

/** Drop a cached thumbnail that failed to render (e.g. a stale signed URL fallback). */
export function forgetEventSnapshot(eid: string): void {
  if (eid) snapshotMem.delete(eid);
}

export interface LogbookPageOpts extends EventsPageOpts {
  channel?: number;
}

/** GET /v3/devices/{id}/logbook — newest-first events + command audit rows. */
export function fetchDeviceLogbook(deviceId: string, opts: LogbookPageOpts = {}): Promise<ListResult<LogbookItem>> {
  return fetchListWithMeta<LogbookItem>(
    `/v3/devices/${encodeURIComponent(deviceId)}/logbook`,
    {
      from: opts.from,
      to: opts.to,
      cursor: opts.cursor ?? undefined,
      limit: opts.limit ?? 100,
      channel: opts.channel,
    },
    opts.signal
  );
}

export function eventFromLogbookItem(item: LogbookItem): EventItem | null {
  if (item.kind !== 'event' || typeof item.ts_us !== 'number') return null;
  return {
    event_ts: item.ts_us,
    event_id: item.event_id ?? '',
    event_type: item.event_type ?? 'manual',
    channel: item.channel,
    correlation_id: item.correlation_id ?? item.eid,
    eid: item.eid,
    has_snapshot: item.has_snapshot,
    record_location: item.record_location,
    record_mode: item.record_mode,
    record_quality: item.record_quality,
    ai: item.ai,
    ai_desc: item.ai_desc,
    payload: item.payload,
  };
}

export function describeLogbookCommand(item: LogbookItem): string {
  const action = item.action ?? 'command';
  const meta = item.meta ?? {};
  const op = typeof meta.op === 'string' ? meta.op : null;
  const id = typeof meta.id === 'string' ? meta.id : null;
  const status = typeof meta.status === 'string' ? meta.status : null;
  const parts = [op ?? action.replace(/^command\./, ''), id, status].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : action;
}

// ---- event type clusters (common/01 §1.2 bits [39:24]; spec 17 §4.2) ----
// NOTE: the server `type` filter matches the FULL event hex (verified live),
// so cluster-level chip filtering is done client-side on loaded pages.

/**
 * 顯示分群。`0x8041 Object Recognition` 拆成四個子類（業主 2026-08-21 裁決）。
 *
 * `object` = 認得是 0x8041、但子序號不在規格登記的四個裡。它必須與 `other` 分開：
 * `other` 是「連 cluster 都不認得」，`object` 是「知道是物件偵測、只是不知道是哪一類」——
 * 兩者在首頁統計的處理相同（都落「其他」格），但標籤與圖示不同，混掉就等於又在猜。
 */
export type ClusterKey =
  | 'motion'
  | 'person'
  | 'vehicle'
  | 'animal'
  | 'package'
  | 'object'
  | 'sound'
  | 'doorbell'
  | 'other';

export interface ClusterInfo {
  key: ClusterKey;
  label: string;
  icon: string;
}

// i18n NOTE（rule 5）：cluster label 不可在模組頂層求值——i18n 切語言後
// 頂層常數不會更新。故內部 map 只存型別/icon/labelKey，label 一律在
// clusterOf() 回傳時以 i18n.t(labelKey) 即時求值（每次呼叫拿當前語言）。
interface ClusterDef {
  key: ClusterKey;
  labelKey: string;
  icon: string;
}

/**
 * cluster bits[39:24] → 顯示分群。
 * ⚠️ `0x8041` **不在這張表**：它要看 bits[7:0] 子序號才分得出人/車/動物/包裹，見 `OBJECT_SUB_DEFS`。
 */
const CLUSTER_DEFS: Record<number, ClusterDef> = {
  0x8020: { key: 'motion', labelKey: 'events.clusterMotion', icon: '🏃' },
  0x8042: { key: 'sound', labelKey: 'events.clusterSound', icon: '🔊' },
  0x8043: { key: 'sound', labelKey: 'events.clusterSound', icon: '🔊' },
  0x8030: { key: 'doorbell', labelKey: 'events.clusterDoorbell', icon: '🔔' },
};

const PERSON_DEF: ClusterDef = { key: 'person', labelKey: 'events.clusterPerson', icon: '🧍' };
const OBJECT_DEF: ClusterDef = { key: 'object', labelKey: 'events.clusterObject', icon: '🔎' };

/**
 * `0x8041 LS Object Recognition` 的**子序號**（bits[7:0]）→ 顯示分群。
 *
 * 權威＝`common/specs/01_event_id_spec.md` §3.20 表 + `04_capability_command_spec.md` §4.2；
 * 機器可讀對照＝`dictionary/capability_dictionary.json` 的 `0x8041.subs = ["0x01".."0x04"]`。
 * 子序號住在 **sub_id = bits[7:0]**，`element`(bits[23:8]) 全是 `0x0000` —— 別拿 element 去分。
 *
 * | 子序號 | 成員 | 64-bit id |
 * |---|---|---|
 * | 0x01 | ObjectDetected·Person  | `0x1100008041000001` |
 * | 0x02 | ObjectDetected·Vehicle | `0x1100008041000002` |
 * | 0x03 | ObjectDetected·Animal  | `0x1100008041000003` |
 * | 0x04 | ObjectDetected·Package | `0x1100008041000004` |
 *
 * ⚠️ **為什麼一定要看子序號**：三平台原本只看 bits[39:24]，`0x8041` 整群被貼「人形」。
 * 2026-08-21 用 dev DDB 實資料對帳：1300 筆「人形」裡有 39 筆車 + 57 筆動物。
 * 依 bits[39:24] 分群本身合規，錯的是標籤與粒度（業主 2026-08-21 裁決拆四類）。
 * iOS `LSDomain.objectSubclassBySub` / Android `LSEvents.OBJECT_SUBS` 是同一張表，改要一起改。
 */
const OBJECT_SUB_DEFS: Record<number, ClusterDef> = {
  0x01: PERSON_DEF,
  0x02: { key: 'vehicle', labelKey: 'events.clusterVehicle', icon: '🚗' },
  0x03: { key: 'animal', labelKey: 'events.clusterAnimal', icon: '🐾' },
  0x04: { key: 'package', labelKey: 'events.clusterPackage', icon: '📦' },
};

const OTHER_CLUSTER_DEF: ClusterDef = { key: 'other', labelKey: 'events.clusterOther', icon: '📌' };

/** ClusterDef → ClusterInfo：label 於此處即時 i18n 求值（rule 5）。 */
function toClusterInfo(def: ClusterDef): ClusterInfo {
  return { key: def.key, label: i18n.t(def.labelKey), icon: def.icon };
}

/**
 * `ClusterKey` → 顯示資訊。⚠️ **不要**改用 `clusterOf('person')` 之類的呼叫來取 —— 那條路是
 * 「event_id / event_type 值 → cluster」，`'person'`/`'sound'` 不是 ch19 的 event_type 值，
 * 會落 OTHER（首頁統計與事件頁 chip 都曾因此把「人形」畫成「其他」）。
 */
const CLUSTER_DEFS_BY_KEY: Record<ClusterKey, ClusterDef> = {
  motion: CLUSTER_DEFS[0x8020],
  person: PERSON_DEF,
  vehicle: OBJECT_SUB_DEFS[0x02],
  animal: OBJECT_SUB_DEFS[0x03],
  package: OBJECT_SUB_DEFS[0x04],
  object: OBJECT_DEF,
  sound: CLUSTER_DEFS[0x8042],
  doorbell: CLUSTER_DEFS[0x8030],
  other: OTHER_CLUSTER_DEF,
};

/** 由 cluster key 取顯示資訊（label 即時 i18n 求值，rule 5）。 */
export function clusterInfoOf(key: ClusterKey): ClusterInfo {
  return toClusterInfo(CLUSTER_DEFS_BY_KEY[key]);
}

/**
 * 粗分類 fallback 字典（spec 17 §4.2：「event_type 粗分類作為 fallback」）：
 * restful 00 ch19 語意 enum → cluster。manual/peer_device/recording_clip 無
 * 對應細分 cluster → OTHER。
 */
const COARSE_TYPE_DEFS: Record<string, ClusterDef> = {
  motion: CLUSTER_DEFS[0x8020],
  doorbell: CLUSTER_DEFS[0x8030],
  // ⚠️ 2026-08-21：物件三條原本 web 完全沒有（iOS/Android 早就有），一筆
  // `event_type = "object_person"` 的人形事件在 web 落「其他」。`object_pet` → animal：
  // 規格成員名是 Animal（野生動物不是寵物），字串鍵本身是既有資料不能改。
  // ⚠️ 本輪只補物件這一族（裁決範圍）；`ring`/`sound` 兩條 web 仍缺，
  // 與物件無關、且會動到 `doorbellRing.ts` 的判定，另案處理。
  object_person: PERSON_DEF,
  object_vehicle: OBJECT_SUB_DEFS[0x02],
  object_pet: OBJECT_SUB_DEFS[0x03],
};

function isHexId(v: string): boolean {
  return v.startsWith('0x') || v.startsWith('0X');
}

/**
 * 型別判定（spec 17 §4.2）：0x.. capability hex → cluster bits [39:24] 對照；
 * 非 hex（如 REST event_type 的 ch19 語意字串）→ 粗分類字典 → OTHER。
 * 防衛：絕不把語意字串餵進 BigInt（W2-03：BigInt("motion") 拋例外曾使
 * 全部事件被標「其他」、型別 chips 失效）。
 */
/**
 * 情境偵測（ls-loop 字典 1.20.0+loop.2，參考 Alfred）：同 cluster、但 element ≠ 0 —— 只看 cluster + sub 會把
 * 人逗留 / 人離開當成一般「人形」、動作停止當成「移動」。key 沿用 person / motion（篩選、統計照舊歸類），名稱另給。
 */
const ACTIVITY_DEFS: Record<string, ClusterDef> = {
  '0x1100008041000101': { key: 'person', labelKey: 'events.personLingering', icon: '🧍' },
  '0x1100008041000201': { key: 'person', labelKey: 'events.personAbsent', icon: '🚪' },
  '0x1100008020000202': { key: 'motion', labelKey: 'events.motionStopped', icon: '⏸' },
};

export function clusterOf(eventIdHex: string): ClusterInfo {
  if (!isHexId(eventIdHex)) return toClusterInfo(COARSE_TYPE_DEFS[eventIdHex] ?? OTHER_CLUSTER_DEF);
  const act = ACTIVITY_DEFS[eventIdHex.toLowerCase()];
  if (act) return toClusterInfo(act);
  try {
    const raw = BigInt(eventIdHex);
    const cluster = Number((raw >> 24n) & 0xffffn);
    if (cluster === 0x8041) {
      // 未登記的子序號（含 0x00）→ `object`「物件」，**不是** `person`（那是本輪要修的謊），
      // 也**不是** `other`（`other` 的語意是「連 cluster 都不認得」）。
      return toClusterInfo(OBJECT_SUB_DEFS[Number(raw & 0xffn)] ?? OBJECT_DEF);
    }
    return toClusterInfo(CLUSTER_DEFS[cluster] ?? OTHER_CLUSTER_DEF);
  } catch {
    return toClusterInfo(OTHER_CLUSTER_DEF);
  }
}

/**
 * 事件列的型別判定入口（W2-03）：`event_id`（hex，REST/WSS 同帶）為準；
 * 缺失或非 hex 時退 `event_type` 粗分類（spec 17 §4.2 fallback 語意）。
 */
export function clusterOfEvent(ev: Pick<EventItem, 'event_id' | 'event_type'>): ClusterInfo {
  if (typeof ev.event_id === 'string' && isHexId(ev.event_id)) return clusterOf(ev.event_id);
  return clusterOf(ev.event_type ?? '');
}

/**
 * spec 17 §4.1：回放事件時間軸「只收攝影機偵測 cluster」。以 event_id_hex 的 cluster bits[39:24] 判定
 * （與 device 韌體 guard 表 recordable、restful SemanticEventType 同一把尺）。用 raw cluster 白名單
 * 而非 ClusterInfo.key：0x8044（遮擋）在 clusterOf 收斂為 other，但仍屬偵測、要納入。
 * 非 hex 事件無法判 cluster → 保留顯示（要隱藏的操作/系統事件皆帶 hex event_id，不會漏）。
 */
const PLAYBACK_DETECTION_CLUSTERS = new Set<number>([0x8020, 0x8030, 0x8041, 0x8042, 0x8043, 0x8044]);
export function isPlaybackDetectionEvent(ev: Pick<EventItem, 'event_id' | 'event_type'>): boolean {
  const id =
    typeof ev.event_id === 'string' && isHexId(ev.event_id)
      ? ev.event_id
      : typeof ev.event_type === 'string' && isHexId(ev.event_type)
        ? ev.event_type
        : null;
  if (id === null) return true;
  try {
    return PLAYBACK_DETECTION_CLUSTERS.has(Number((BigInt(id) >> 24n) & 0xffffn));
  } catch {
    return true;
  }
}

// ---- timeline display ordering (W5/APP-03) ----

/** A device-tagged event row as rendered by the timeline. */
export interface TimelineRow {
  deviceId: string;
  ev: EventItem;
}

/**
 * Display order is NEWEST-FIRST (倒序). The events endpoint pages ascending
 * (restful 00 §13/ch19: 排序 event_sk 升冪；client 倒序顯示自行處理) — the
 * single comparator below is the one sort path for both REST pages and live
 * WSS-merged events (APP specs 17 §4.1), so newer "load more" pages and
 * realtime inserts both land at the top while older data sinks to the bottom.
 */
export function compareRowsNewestFirst(a: TimelineRow, b: TimelineRow): number {
  if (b.ev.event_ts !== a.ev.event_ts) return b.ev.event_ts - a.ev.event_ts;
  // Stable tiebreak for equal timestamps so React keys don't reshuffle.
  const ka = `${a.deviceId}#${a.ev.correlation_id ?? ''}`;
  const kb = `${b.deviceId}#${b.ev.correlation_id ?? ''}`;
  return ka < kb ? -1 : ka > kb ? 1 : 0;
}

/** 事件驗證閘道判「未確認」（payload.verify；本地 AI 跟攝影機不一致、沒有付費第 4 層）：
 *  雲端照存、不推播；列表照顯示並標「未確認」，先收數據再決定最後要不要顯示（ls-loop 2026-10-11）。 */
export function isUnconfirmed(e: { payload?: Record<string, unknown> }): boolean {
  return e.payload?.verify === 'unconfirmed';
}

/** Secondary descriptive text for an event row (payload label/confidence etc.). */
export function describeEvent(e: EventItem): string {
  const p = e.payload ?? {};
  const parts: string[] = [];
  if (typeof p.label === 'string') parts.push(p.label);
  if (typeof p.confidence === 'number') parts.push(`${Math.round(p.confidence * 100)}%`);
  if (typeof p.version === 'string') parts.push(`v${p.version}`);
  return parts.join(' · ');
}

// NOTE (WEB3-F2): the local DeviceSummary copy that used to live here is gone —
// it had no display_name and mistyped `online` as boolean (the wire `online` is
// an OBJECT, so `d.online === false` could never fire). Pages must import the
// canonical DeviceSummary from features/devices/deviceModel instead.

// ---- local-day window helpers (µs epoch; day boundary = local timezone, 17 §2.2/B9) ----

export interface UsWindow {
  fromUs: number;
  toUs: number;
}

/** "YYYY-MM-DD" (local) → [local midnight, +24h) in µs. */
export function dayWindowUs(dateStr: string): UsWindow {
  const [y, m, d] = dateStr.split('-').map(Number);
  const start = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1).getTime() * 1000;
  return { fromUs: start, toUs: start + 86_400_000_000 };
}

/** Date → local "YYYY-MM-DD" for <input type=date>. */
export function toDateInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
